// Shared auth-cookie configuration for every Supabase client (browser,
// server, middleware).
//
// WHY THIS EXISTS: without an explicit `maxAge`, some of the auth-token
// cookies `@supabase/ssr` writes end up as *session cookies* — the browser
// deletes them the moment the window/tab (or a PWA/home-screen app) is
// closed, and the user is silently logged out on their next visit. Setting a
// long, explicit lifetime makes the login "stick" across browser restarts,
// which is what users expect.
//
// PERSISTENT_MAX_AGE is 400 days — the maximum cookie lifetime Chrome (and
// other browsers, per the Cookie spec's 400-day cap) will honor. Anything
// larger is silently clamped, so this is the longest a "keep me signed in"
// session can safely last.
export const PERSISTENT_MAX_AGE = 60 * 60 * 24 * 400 // 400 days, in seconds

// localStorage key mirrored from the login "Remember me" checkbox. Only an
// explicit opt-OUT ("false") makes sessions browser-scoped; the default is to
// persist so no one is unexpectedly logged out.
export const REMEMBER_ME_KEY = "booth_remember_me"

/**
 * Reads the user's "Remember me" preference on the client.
 *
 * Defaults to `true` (persist) — the reported bug was users being logged out
 * on browser exit, so persistence is the safe default. Only a stored value of
 * exactly "false" opts into a browser-scoped session.
 */
export function shouldPersistSession(): boolean {
  if (typeof window === "undefined") return true
  try {
    return window.localStorage.getItem(REMEMBER_ME_KEY) !== "false"
  } catch {
    // Storage blocked (private mode / cross-origin) — default to persistent.
    return true
  }
}

/**
 * Cookie options for a Supabase client. When the session should persist we
 * hand the library an explicit `maxAge`; otherwise we omit it so the tokens
 * remain session cookies (cleared when the browser closes).
 */
export function authCookieOptions(persist = true): { maxAge?: number } {
  return persist ? { maxAge: PERSISTENT_MAX_AGE } : {}
}
